import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineLocationMarker, HiCheck } from 'react-icons/hi';
import { profileAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getFriendlyError } from '../utils/errorMessage';
import LocationSelect from '../components/LocationSelect';
import './ProfileSetup.css';

const ProfileSetup = () => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    interestedIn: '',
    location: '',
    latitude: null,
    longitude: null,
    bio: '',
    interests: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const toast = useToast();

  const getLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser. Please enter your location manually.');
      return;
    }

    setGettingLocation(true);
    setError(''); // Clear any previous errors
    
    // Try with high accuracy first
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        setFormData(prev => ({
          ...prev,
          latitude,
          longitude
        }));

        // Use OpenStreetMap for reverse geocoding
        await reverseGeocodeWithOSM(latitude, longitude);

        setGettingLocation(false);
      },
      (error) => {
        // If high accuracy fails, try again with lower accuracy
        if (error.code === error.TIMEOUT) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;

              setFormData(prev => ({
                ...prev,
                latitude,
                longitude
              }));

              await reverseGeocodeWithOSM(latitude, longitude);
              setGettingLocation(false);
            },
            (retryError) => {
              let errorMessage = 'Unable to get your location. ';
              
              switch(retryError.code) {
                case retryError.PERMISSION_DENIED:
                  errorMessage += 'Location access was denied. Please enable it in your browser settings.';
                  break;
                case retryError.POSITION_UNAVAILABLE:
                  errorMessage += 'Location information is unavailable.';
                  break;
                case retryError.TIMEOUT:
                  errorMessage += 'Location request timed out. Please enter your location manually.';
                  break;
                default:
                  errorMessage += 'Please enter it manually.';
              }
              
              setError(errorMessage);
              setGettingLocation(false);
            },
            {
              enableHighAccuracy: false,
              timeout: 30000,
              maximumAge: 300000 // Accept cached location up to 5 minutes old
            }
          );
        } else {
          // For non-timeout errors, show error immediately
          let errorMessage = 'Unable to get your location. ';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Location access was denied. Please enable it in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information is unavailable.';
              break;
            default:
              errorMessage += 'Please enter it manually.';
          }
          
          setError(errorMessage);
          setGettingLocation(false);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // Reduced from 15s to 10s for first attempt
        maximumAge: 0
      }
    );
  };

  const reverseGeocodeWithOSM = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'KwikLuv/1.0'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Geocoding failed');
      }
      
      const data = await response.json();

      // Priority: suburb > neighbourhood > city_district > quarter > city > town > village > municipality > county
      const suburb = data.address?.suburb;
      const neighbourhood = data.address?.neighbourhood;
      const cityDistrict = data.address?.city_district;
      const quarter = data.address?.quarter;
      const city = data.address?.city || 
                  data.address?.town || 
                  data.address?.village;
      const municipality = data.address?.municipality;
      const county = data.address?.county;
      
      // Use the most specific location available
      let locationName = suburb || neighbourhood || cityDistrict || quarter || city || municipality;
      
      // If county is not a province name and doesn't contain "Ward", use it
      const provinceNames = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West'];
      if (!locationName && county && !provinceNames.includes(county) && !county.includes('Ward')) {
        locationName = county;
      }
      
      // Clean up location name - remove "Ward XX" patterns
      if (locationName) {
        locationName = locationName.replace(/\s+Ward\s+\d+/gi, '').trim();
      }
      
      // Fallback to first part of display name if nothing found
      if (!locationName || locationName === 'Unknown') {
        const firstPart = data.display_name?.split(',')[0]?.trim();
        // Clean the first part too
        locationName = firstPart?.replace(/\s+Ward\s+\d+/gi, '').trim();
      }
      
      setFormData(prev => ({
        ...prev,
        location: locationName || ''
      }));

      if (!locationName) {
        setError("We found your position but couldn't name the area. Please pick your city or town.");
      }
    } catch (error) {
      // Couldn't resolve a readable place name. Keep coordinates for matching,
      // but don't drop raw lat/lon into the visible location field.
      setError("We couldn't determine your area automatically. Please enter your location manually.");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!formData.gender) {
      setError('Please select your gender');
      return;
    }
    
    if (!formData.interestedIn) {
      setError('Please select who you are interested in');
      return;
    }
    
    if (!formData.location || !formData.latitude || !formData.longitude) {
      setError('Please select a location from the suggestions');
      return;
    }
    
    setLoading(true);
    
    try {
      const profileData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
      };
      
      await profileAPI.updateProfile(profileData);
      toast.success('Profile completed! You can now browse profiles');
      navigate('/discover');
    } catch (err) {
      setError(getFriendlyError(err, 'Failed to create your profile. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-setup-container">
      <div className="profile-setup-card">
        <button
          type="button"
          className="profile-setup-exit"
          onClick={() => logout()}
          disabled={loading}
        >
          ← Sign out
        </button>
        <h2>Complete Your Profile</h2>
        <p className="profile-setup-subtitle">
          Just a few details so we can show you the right people.
        </p>
        {gettingLocation && (
          <div style={{
            padding: '12px',
            backgroundColor: '#dbeafe',
            color: '#1e3a8a',
            borderRadius: '8px',
            marginBottom: '16px',
            textAlign: 'center'
          }} role="status" aria-live="polite">
            <HiOutlineLocationMarker aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: '6px' }} />
            Getting your location...
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label htmlFor="setup-name">Display name <span className="required">*</span></label>
            <input
              id="setup-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              placeholder="The name others will see"
              required
            />
          </div>
          <div className="input-group">
            <label>Age <span className="required">*</span></label>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              min="18"
              max="99"
              disabled={loading}
              placeholder="18+"
              required
            />
          </div>
          <div className="input-group">
            <label>I am <span className="required">*</span></label>
            <select 
              name="gender" 
              value={formData.gender} 
              onChange={handleChange}
              disabled={loading}
              required
            >
              <option value="">Select gender</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div className="input-group">
            <label>Interested in <span className="required">*</span></label>
            <select 
              name="interestedIn" 
              value={formData.interestedIn} 
              onChange={handleChange}
              disabled={loading}
              required
            >
              <option value="">Select preference</option>
              <option value="MALE">Men</option>
              <option value="FEMALE">Women</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div className="input-group">
            <label>Location <span className="required">*</span></label>
            <LocationSelect
              value={formData.location}
              onChange={(name, lat, lon) => {
                setFormData(prev => ({ 
                  ...prev, 
                  location: name,
                  latitude: lat, 
                  longitude: lon 
                }));
              }}
              disabled={loading}
            />
            <button
              type="button"
              className="use-location-btn"
              onClick={getLocation}
              disabled={loading || gettingLocation}
            >
              <HiOutlineLocationMarker aria-hidden="true" />
              {gettingLocation ? 'Getting location…' : 'Use my current location'}
            </button>
            <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginTop: '8px', display: 'block' }}>
              {formData.latitude && formData.longitude ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#22c55e' }}>
                  <HiCheck aria-hidden="true" /> Location set
                </span>
              ) : (
                'Search for your city or town, or use your current location'
              )}
            </small>
          </div>
          <div className="input-group">
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows="4"
              placeholder="Tell us about yourself..."
              disabled={loading}
            />
          </div>
          <div className="input-group">
            <label>Interests</label>
            <input
              type="text"
              name="interests"
              value={formData.interests}
              onChange={handleChange}
              placeholder="e.g., hiking, reading, cooking"
              disabled={loading}
            />
          </div>
          {error && (
            <div className="error" role="alert" aria-live="assertive" style={{
              padding: '12px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%' }}
            disabled={loading || gettingLocation}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetup;
